import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  useToast, 
  Badge,
  VStack,
  Heading,
  Text,
  Card,
  CardBody,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  HStack,
  Wrap,
  WrapItem
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons'
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';
import DataTable from '../../common/DataTable/DataTable';

const Content = () => {
  const [content, setContent] = useState([]);
  const [filteredContent, setFilteredContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [contentModels, setContentModels] = useState([]);
  const [locales, setLocales] = useState([]);
  const [filters, setFilters] = useState({ 
    site_id: [], 
    status: [], 
    locale_id: [], 
    content_model_id: [] 
  });
  
  const toast = useToast();
  const navigate = useNavigate();

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const cardBg = useColorModeValue('white', 'gray.700')
  
  // Get tenant ID from session storage
  const user = JSON.parse(sessionStorage.getItem('user'))
  const tenantId = user?.tenant_ids?.[0]
  
  const statusColors = {
    draft: 'gray',
    published: 'green',
    pending: 'orange',
    archived: 'red'
  };

  // Enhanced Table Column Definitions for multilingual content
  const columns = [
    { 
      key: 'default_title', 
      label: 'Title',
      render: (value, row) => (
        <VStack align="start" spacing={1}>
          <Text fontWeight="medium" noOfLines={1}>
            {value || 'Untitled'}
          </Text>
          <Text fontSize="xs" color="gray.500">ID: #{row.id}</Text>
          {row.translations && row.translations.length > 0 && (
            <HStack spacing={1}>
              <Text fontSize="xs" color="blue.500">
                {row.translations.length} translation(s)
              </Text>
            </HStack>
          )}
        </VStack>
      )
    },
    { 
      key: 'content_model_name', 
      label: 'Content Model',
      render: (value, row) => (
        <Badge colorScheme="purple" variant="subtle">
          {value || getContentModelName(row.content_model_id)}
        </Badge>
      )
    },
    { 
      key: 'site_name', 
      label: 'Site',
      render: (value, row) => (
        <Text fontSize="sm">{value || getSiteName(row.site_id)}</Text>
      )
    },
    { 
      key: 'translations', 
      label: 'Languages',
      render: (translations) => (
        <Wrap spacing={1}>
          {translations && translations.length > 0 ? (
            translations.map((translation, index) => (
              <WrapItem key={index}>
                <Badge 
                  colorScheme="orange" 
                  variant="subtle" 
                  fontSize="xs"
                  title={`${translation.locale_name} - ${translation.status}`}
                >
                  {translation.locale_code}
                </Badge>
              </WrapItem>
            ))
          ) : (
            <Badge colorScheme="gray" variant="outline" fontSize="xs">
              No translations
            </Badge>
          )}
        </Wrap>
      )
    },
    { 
      key: 'status', 
      label: 'Status',
      render: (value) => (
        <Badge colorScheme={statusColors[value] || 'gray'} variant="subtle">
          {value}
        </Badge>
      )
    },
    { 
      key: 'updated_at', 
      label: 'Last Modified',
      render: (value) => (
        <Text fontSize="sm" color="gray.600">
          {new Date(value).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </Text>
      )
    },
  ];

  // Fetch all necessary data
  useEffect(() => {
    fetchSites();
    fetchContentModels();
    fetchLocales();
  }, []);

  // Fetch content when filters change or initial data loads
  useEffect(() => {
    if (sites.length && contentModels.length && locales.length) fetchContent();
  }, [sites, contentModels, locales, filters]);

  const fetchSites = async () => {
    try {
      const response = await apiClient(`/sites/${tenantId}`);
      setSites(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching sites:', error);
      toast({
        title: 'Error fetching sites',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const fetchContentModels = async () => {
    try {
      const response = await apiClient(`/content-models/${tenantId}`);
      setContentModels(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching content models:', error);
      toast({
        title: 'Error fetching content models',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const fetchLocales = async () => {
    try {
      const response = await apiClient(`/locales/${tenantId}`);
      setLocales(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching locales:', error);
      toast({
        title: 'Error fetching locales',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const fetchContent = async () => {
    try {
      setLoading(true);
      
      const query = new URLSearchParams();
      if (filters.site_id.length) query.append('site_id', filters.site_id.join(','));
      if (filters.status.length) query.append('status', filters.status.join(','));
      if (filters.locale_id.length) query.append('locale_id', filters.locale_id.join(','));
      if (filters.content_model_id.length) query.append('content_model_id', filters.content_model_id.join(','));
      
      const response = await apiClient(`/content-entries/${tenantId}?${query.toString()}`);
      
      // Handle both direct array and nested data structure
      const contentData = Array.isArray(response) ? response : (response.data || response.entries || []);
      
      setContent(contentData);
      setFilteredContent(contentData);
    } catch (error) {
      console.error('Error fetching content:', error);
      toast({
        title: 'Error fetching content',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const getContentModelName = (id) => {
    const contentModel = contentModels.find(cm => cm.id === id);
    return contentModel ? contentModel.name : 'Unknown';
  };

  const getSiteName = (id) => {
    const site = sites.find(s => s.id === id);
    return site ? site.name : 'Unknown';
  };

  const getLocaleName = (id) => {
    const locale = locales.find(l => l.id === id);
    return locale ? `${locale.name} (${locale.code})` : 'Unknown';
  };

  // Handle search functionality - search in both default title and translations
  const handleSearch = (searchTerm) => {
    if (!searchTerm) {
      setFilteredContent(content);
      return;
    }
    
    const lowercaseSearch = searchTerm.toLowerCase();
    const filtered = content.filter(item => {
      // Search in default title
      if (item.default_title && item.default_title.toLowerCase().includes(lowercaseSearch)) {
        return true;
      }
      
      // Search in translations
      if (item.translations && item.translations.length > 0) {
        return item.translations.some(translation => 
          translation.title && translation.title.toLowerCase().includes(lowercaseSearch)
        );
      }
      
      return false;
    });
    
    setFilteredContent(filtered);
  };

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Create/Edit/Delete handlers
  const handleEdit = (item) => {
    navigate(`/content/edit/${item.id}`);
  };

  const handleView = (item) => {
    navigate(`/content/view/${item.id}`);
  };

  const handleCreate = () => {
    navigate('/content/create');
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Are you sure you want to delete this content entry and all its translations?')) {
      return;
    }
    
    try {
      await apiClient(`/content-entries/${tenantId}/${item.id}`, {
        method: 'DELETE'
      });
      
      toast({
        title: 'Success',
        description: 'Content deleted successfully',
        status: 'success',
        duration: 3000,
      });
      
      fetchContent();
      
    } catch (error) {
      console.error('Error deleting content:', error);
      toast({
        title: 'Error deleting content',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Create filter configuration for DataTable
  const filterConfig = {
    site_id: {
      options: sites.map(site => ({ id: site.id, name: site.name })),
      colorScheme: 'blue'
    },
    content_model_id: {
      options: contentModels.map(model => ({ id: model.id, name: model.name })),
      colorScheme: 'purple'
    },
    status: {
      options: [
        { id: 'draft', name: 'Draft' },
        { id: 'published', name: 'Published' },
        { id: 'archived', name: 'Archived' }
      ],
      colorScheme: 'green'
    },
    locale_id: {
      options: locales.map(locale => ({ id: locale.id, name: `${locale.name} (${locale.code})` })),
      colorScheme: 'orange'
    }
  };

  // Custom filter display names
  const filterDisplayNames = {
    site_id: 'Site',
    content_model_id: 'Content Model',
    status: 'Status',
    locale_id: 'Language'
  };

  // Calculate statistics from multilingual content
  const getContentStats = () => {
    const total = content.length;
    const published = content.filter(item => item.status === 'published').length;
    const draft = content.filter(item => item.status === 'draft').length;
    
    // Count total translations
    const totalTranslations = content.reduce((sum, item) => {
      return sum + (item.translations ? item.translations.length : 0);
    }, 0);
    
    return { total, published, draft, totalTranslations };
  }

  const stats = getContentStats();

  return (
    <Container maxW="container.2xl" py={6}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Content</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={6} align="stretch">
        {/* Page Header */}
        <Box>
          <Heading size="lg" mb={2}>Multilingual Content</Heading>
          <Text color="gray.500">
            Manage your multilingual content entries. Create, edit, and publish content with translations across your sites.
          </Text>
        </Box>

        {/* Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Total Content</StatLabel>
                <StatNumber color="blue.500">{stats.total}</StatNumber>
                <StatHelpText>Content entries</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Published</StatLabel>
                <StatNumber color="green.500">{stats.published}</StatNumber>
                <StatHelpText>Live content</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Drafts</StatLabel>
                <StatNumber color="gray.500">{stats.draft}</StatNumber>
                <StatHelpText>Work in progress</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Translations</StatLabel>
                <StatNumber color="orange.500">{stats.totalTranslations}</StatNumber>
                <StatHelpText>Total translations</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Enhanced DataTable */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardBody p={0}>
            <DataTable
              columns={columns}
              data={filteredContent}
              onSearch={handleSearch}
              onEdit={handleEdit}
              onCreate={handleCreate}
              onDelete={handleDelete}
              searchPlaceholder="Search content by title (searches all translations)..."
              isLoading={loading}
              title="Multilingual Content Management"
              createButtonText="Create New Content"
              emptyStateTitle="No content found"
              emptyStateDescription={
                filteredContent.length === 0 && content.length > 0
                  ? "No content matches your search or filter criteria. Try adjusting your filters."
                  : "Get started by creating your first multilingual content entry."
              }
              loadingText="Loading content..."
              // Filter-related props
              filterConfig={filterConfig}
              onFilterChange={handleFilterChange}
              activeFilters={filters}
              filterDisplayNames={filterDisplayNames}
              // Custom action props
              customActions={[
                {
                  label: 'View',
                  icon: 'view',
                  onClick: handleView,
                  colorScheme: 'blue'
                }
              ]}
            />
          </CardBody>
        </Card>
      </VStack>
    </Container>
  );
};

export default Content;
